import { useEffect, useState } from "react";
import type { ResourceKind } from "@checklist/shared";
import { ErrorBanner, fieldCls, labelCls } from "./AuthShell.js";
import { Button } from "./ui/Button.js";
import { Modal } from "./ui/Modal.js";
import { showToast } from "./ui/Toast.js";
import {
  createShareLink,
  fetchShareLinks,
  type ApiChecklistRole,
} from "../lib/api.js";
import {
  exportPdf,
  PdfExportError,
  warmPdfExport,
} from "../lib/pdf/exportPdf.js";
import {
  DEFAULT_PDF_OPTIONS,
  PDF_OPTIONS_STORAGE_KEY,
  type PdfDocModel,
  type PdfPaper,
  type PdfUserOptions,
} from "../lib/pdf/model.js";

export interface PdfExportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Shown as the dialog subtitle, e.g. "Iceland - March". */
  docTitle: string;
  /** Called with the chosen options at download time (draft state, P-12). */
  buildDoc: (options: PdfUserOptions, qrUrl?: string) => PdfDocModel;
  /** Checklists true; Lists/Templates false - hides the progress + attribution controls. */
  supportsProgress: boolean;
  /** Checklists with memberCount >= 2; otherwise false. */
  supportsAttribution: boolean;
  /** Owners may mint/reuse a share link for the printed QR code. */
  shareResource?: { kind: ResourceKind; id: string; isOwner: boolean };
}

type QrAccess = "read" | "write" | "request";

function resourceSegment(kind: ResourceKind): string {
  return kind === "list" ? "lists" : kind === "template" ? "templates" : "checklists";
}

function canonicalUrl(resource: NonNullable<PdfExportDialogProps["shareResource"]>): string {
  return `${window.location.origin}/${resourceSegment(resource.kind)}/${resource.id}`;
}

function storedOptions(): PdfUserOptions {
  try {
    const raw = localStorage.getItem(PDF_OPTIONS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PDF_OPTIONS };
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { ...DEFAULT_PDF_OPTIONS };

    const values = parsed as Record<string, unknown>;
    const paper: PdfPaper =
      values.paper === "letter" || values.paper === "a4"
        ? values.paper
        : DEFAULT_PDF_OPTIONS.paper;

    return {
      paper,
      showProgress:
        typeof values.showProgress === "boolean"
          ? values.showProgress
          : DEFAULT_PDF_OPTIONS.showProgress,
      includeNotes:
        typeof values.includeNotes === "boolean"
          ? values.includeNotes
          : DEFAULT_PDF_OPTIONS.includeNotes,
      includeAttribution:
        typeof values.includeAttribution === "boolean"
          ? values.includeAttribution
          : DEFAULT_PDF_OPTIONS.includeAttribution,
      colorAccents:
        typeof values.colorAccents === "boolean"
          ? values.colorAccents
          : DEFAULT_PDF_OPTIONS.colorAccents,
    };
  } catch {
    return { ...DEFAULT_PDF_OPTIONS };
  }
}

function constrainOptions(
  options: PdfUserOptions,
  supportsProgress: boolean,
  supportsAttribution: boolean
): PdfUserOptions {
  return {
    ...options,
    showProgress: supportsProgress ? options.showProgress : false,
    includeAttribution:
      supportsProgress && supportsAttribution
        ? options.includeAttribution
        : false,
  };
}

export function PdfExportDialog({
  open,
  onClose,
  docTitle,
  buildDoc,
  supportsProgress,
  supportsAttribution,
  shareResource,
}: PdfExportDialogProps) {
  const [options, setOptions] = useState<PdfUserOptions>(DEFAULT_PDF_OPTIONS);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [qrAccess, setQrAccess] = useState<QrAccess>("read");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setOptions(
      constrainOptions(storedOptions(), supportsProgress, supportsAttribution)
    );
    setOptionsLoaded(true);
  }, []);

  useEffect(() => {
    setOptions((current) =>
      constrainOptions(current, supportsProgress, supportsAttribution)
    );
  }, [supportsAttribution, supportsProgress]);

  useEffect(() => {
    if (!optionsLoaded) return;
    try {
      localStorage.setItem(PDF_OPTIONS_STORAGE_KEY, JSON.stringify(options));
    } catch {
      // Local storage is an optional convenience; exporting still works without it.
    }
  }, [options, optionsLoaded]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setQrAccess("read");
    void warmPdfExport();
  }, [open]);

  async function resolveQrUrl(): Promise<string | undefined> {
    if (!shareResource) return undefined;
    const fallback = canonicalUrl(shareResource);
    if (!shareResource.isOwner || qrAccess === "request") return fallback;

    const role: ApiChecklistRole = qrAccess === "write" ? "write" : "read";
    const links = await fetchShareLinks(shareResource.kind, shareResource.id);
    const existing = links.find(
      (link) =>
        !link.revokedAt &&
        !link.expiresAt &&
        !link.requireSignIn &&
        link.role === role
    );
    const token = existing
      ? existing.token
      : (
          await createShareLink(shareResource.kind, shareResource.id, {
            role,
            requireSignIn: false,
            label: "PDF export",
          })
        ).token;
    return `${window.location.origin}/s/${token}`;
  }

  async function onDownload() {
    const selectedOptions = constrainOptions(
      options,
      supportsProgress,
      supportsAttribution
    );
    setBusy(true);
    setError(null);

    try {
      const qrUrl = await resolveQrUrl();
      await exportPdf(buildDoc(selectedOptions, qrUrl), selectedOptions);
      onClose();
      showToast("PDF downloaded");
    } catch (err) {
      setError(
        err instanceof PdfExportError
          ? err.message
          : err instanceof Error
            ? err.message
          : "Could not create the PDF. Please try again."
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} labelledBy="pdfExportTitle">
      <h2 id="pdfExportTitle" className="text-lg font-semibold text-ink">
        Download PDF
      </h2>
      <p className="mt-1 text-sm text-muted">{docTitle}</p>
      {!supportsProgress ? (
        <p className="mt-1 text-xs text-muted-sm">Exports what&apos;s on screen now.</p>
      ) : null}

      <div className="mt-4">
        <ErrorBanner message={error} />
      </div>

      <form
        className="mt-6 space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          void onDownload();
        }}
      >
        {supportsProgress ? (
          <fieldset>
            <legend className={labelCls}>Checkboxes</legend>
            <div className="mt-2 space-y-2">
              <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
                <input
                  type="radio"
                  name="pdfProgress"
                  checked={!options.showProgress}
                  onChange={() =>
                    setOptions((current) => ({
                      ...current,
                      showProgress: false,
                    }))
                  }
                />
                <span>Blank - print and check off by hand</span>
              </label>
              <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
                <input
                  type="radio"
                  name="pdfProgress"
                  checked={options.showProgress}
                  onChange={() =>
                    setOptions((current) => ({
                      ...current,
                      showProgress: true,
                    }))
                  }
                />
                <span>Show current progress</span>
              </label>
            </div>
          </fieldset>
        ) : null}

        {shareResource?.isOwner ? (
          <fieldset>
            <legend className={labelCls}>Anyone who scans the QR code can:</legend>
            <div className="mt-2 space-y-2">
              <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
                <input
                  type="radio"
                  name="pdfQrAccess"
                  checked={qrAccess === "read"}
                  onChange={() => setQrAccess("read")}
                />
                <span>View only</span>
              </label>
              <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
                <input
                  type="radio"
                  name="pdfQrAccess"
                  checked={qrAccess === "write"}
                  onChange={() => setQrAccess("write")}
                />
                <span>Edit</span>
              </label>
              <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
                <input
                  type="radio"
                  name="pdfQrAccess"
                  checked={qrAccess === "request"}
                  onChange={() => setQrAccess("request")}
                />
                <span>Must request access</span>
              </label>
            </div>
            {qrAccess === "write" ? (
              <p className="mt-2 text-xs font-medium text-amber-900">
                Anyone with the printed copy can edit this list without signing in.
              </p>
            ) : null}
          </fieldset>
        ) : null}

        <div>
          <label htmlFor="pdfPaper" className={labelCls}>
            Paper size
          </label>
          <select
            id="pdfPaper"
            className={fieldCls}
            value={options.paper}
            onChange={(event) =>
              setOptions((current) => ({
                ...current,
                paper: event.target.value as PdfPaper,
              }))
            }
          >
            <option value="letter">Letter</option>
            <option value="a4">A4</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={options.includeNotes}
              onChange={(event) =>
                setOptions((current) => ({
                  ...current,
                  includeNotes: event.target.checked,
                }))
              }
            />
            Include item notes
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={options.colorAccents}
              onChange={(event) =>
                setOptions((current) => ({
                  ...current,
                  colorAccents: event.target.checked,
                }))
              }
            />
            Color accents
          </label>
          {supportsProgress && supportsAttribution ? (
            <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={options.includeAttribution}
                onChange={(event) =>
                  setOptions((current) => ({
                    ...current,
                    includeAttribution: event.target.checked,
                  }))
                }
              />
              Include who checked each item
            </label>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? "Preparing..." : "Download PDF"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
